/** Thin typed wrapper over the service. Errors carry the message the API actually returned. */

export interface Client {
  slug: string;
  appName: string;
  packageName: string;
  /** What iOS builds. The same as packageName unless the client set its own. */
  bundleId: string;
  hasIosFirebase: boolean;
  appDomain: string;
  appId: number;
  versionName: string | null;
  aboutMenuTitle: string | null;
  iconBackground: string;
  iconScale: number;
  iconTrim: boolean;
  templateRef: string | null;
  lastVersionCode: number;
  nextVersionCode: number;
  hasKeystore: boolean;
  hasIosSigning: boolean;
  hasAppStoreKey: boolean;
  lastJob: { id: number; state: JobState; verdict: string | null } | null;
}

export interface AppStoreKey {
  present?: false;
  issuerId: string;
  keyId: string;
  name: string | null;
  uploadedAt: number;
  uploadedBy: string;
  replacedPrevious?: boolean;
  /** False when Apple could not be reached, so the key was stored without being tried. */
  checked?: boolean;
  /** Whether their account already has an app with this bundle id. */
  appExists?: boolean | null;
  note?: string;
}

export interface IosSigning {
  present?: false;
  teamId: string;
  profileName: string;
  bundleId: string;
  expires: string | null;
  certificateName: string | null;
  certificateFile: string;
  profileFile: string;
  uploadedAt: number;
  uploadedBy: string;
  replacedPrevious?: boolean;
  passwordChecked?: boolean;
  note?: string;
}

export interface Platforms {
  android: boolean;
  ios: boolean;
  iosRef: string;
}

export interface Agent {
  id: number;
  name: string;
  platforms: string[];
  enabled: boolean;
  online: boolean;
  lastSeenAt: number | null;
  xcodeVersion: string | null;
  osVersion: string | null;
  hostName: string | null;
  maxJobs: number;
  busyWith: number[];
  /** Present only in the reply that issued it. It is stored hashed and never shown again. */
  token?: string;
}

export type JobState = 'queued' | 'running' | 'passed' | 'failed' | 'cancelled';

export interface Artifact {
  kind: string;
  name: string;
  bytes: number | null;
  /** Set once the janitor removed the file to free space; the build stays in the history. */
  prunedAt: number | null;
  url: string;
}

export interface Storage {
  disk: { totalBytes: number; freeBytes: number; usedFraction: number; belowFloor: boolean } | null;
  keepPerClient: number;
  queuePaused: boolean;
  byClient: { slug: string; bytes: number; releases: number }[];
  orphans: { dir: string; bytes: number }[];
}

export interface Job {
  id: number;
  slug: string;
  ref: string;
  commit: string | null;
  state: JobState;
  verdict: string | null;
  exitCode: number | null;
  failure: { step: string; message: string } | null;
  options: { debug?: boolean; skipSmoke?: boolean; noRelaunch?: boolean };
  platform: 'android' | 'ios';
  requestedAgentId: number | null;
  agentId: number | null;
  versionName: string | null;
  versionCode: number | null;
  worker: string | null;
  requestedBy: string;
  queuedAt: number;
  startedAt: number | null;
  finishedAt: number | null;
  artifacts: Artifact[];
}

export interface Template {
  repoUrl: string;
  defaultRef: string;
  branches: string[];
  tags: string[];
  fetchedAt: number;
  error?: string;
}

export interface KeystoreResult {
  slug: string;
  storeType: string;
  alias: string;
  sha256: string | null;
  replacedPrevious?: boolean;
  generated?: boolean;
  note: string;
}

export type Role = 'admin' | 'member';

export interface User {
  id: number;
  email: string;
  name: string;
  role: Role;
  disabled: boolean;
  mustChangePassword: boolean;
  createdAt: number;
  createdBy: string;
  lastLoginAt: number | null;
}

export interface Session {
  user: User;
  auth: { mode: 'password' | 'development'; minPasswordLength: number };
}

export interface DeviceSlot {
  slot: number;
  avd: string;
  port: number;
  jobId: number | null;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly errors: string[] = []) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare JSON when there is JSON. A POST carrying this header with an empty body is
  // rejected by the server's body parser with a 400 -- which is what silently broke cancelling
  // a build, and would have broken signing out.
  const isJson = init?.body !== undefined && !(init.body instanceof FormData);
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: isJson ? { 'Content-Type': 'application/json', ...init?.headers } : init?.headers,
  });
  const text = await response.text();
  const body = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new ApiError(body?.error ?? response.statusText, response.status, body?.errors ?? []);
  }
  return body as T;
}

export const api = {
  clients: () => request<Client[]>('/api/clients'),
  createClient: (form: FormData) => request<{ slug: string }>('/api/clients', { method: 'POST', body: form }),
  patchClient: (slug: string, patch: {
    appName?: string;
    packageName?: string;
    appDomain?: string;
    appId?: number;
    bundleId?: string | null;
    versionName?: string | null;
    aboutMenuTitle?: string | null;
    templateRef?: string | null;
    iconBackground?: string | null;
    iconScale?: number;
    iconTrim?: boolean;
    lastVersionCode?: number;
  }) =>
    request<Client>(`/api/clients/${slug}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  /** A whole edit in one request: a package name and its google-services.json only validate together. */
  updateClient: (slug: string, body: FormData) =>
    request<Client>(`/api/clients/${slug}`, { method: 'PUT', body }),
  agents: () => request<Agent[]>('/api/agents'),
  addAgent: (name: string, platforms: string[] = ['ios']) =>
    request<Agent>('/api/agents', { method: 'POST', body: JSON.stringify({ name, platforms }) }),
  updateAgent: (id: number, patch: { name?: string; enabled?: boolean; maxJobs?: number }) =>
    request<Agent>(`/api/agents/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  regenerateAgentToken: (id: number) =>
    request<{ token: string }>(`/api/agents/${id}/token`, { method: 'POST' }),
  removeAgent: (id: number) => request<{ removed: string }>(`/api/agents/${id}`, { method: 'DELETE' }),
  iosSigning: (slug: string) => request<IosSigning>(`/api/clients/${slug}/ios-signing`),
  uploadIosSigning: (slug: string, body: FormData) =>
    request<IosSigning>(`/api/clients/${slug}/ios-signing`, { method: 'PUT', body }),
  appStoreKey: (slug: string) => request<AppStoreKey>(`/api/clients/${slug}/appstore-key`),
  uploadAppStoreKey: (slug: string, body: FormData) =>
    request<AppStoreKey>(`/api/clients/${slug}/appstore-key`, { method: 'PUT', body }),
  template: (refresh = false) => request<Template>(`/api/template${refresh ? '?refresh=1' : ''}`),
  uploadKeystore: (slug: string, form: FormData) =>
    request<KeystoreResult>(`/api/clients/${slug}/keystore`, { method: 'PUT', body: form }),
  generateKeystore: (slug: string) =>
    request<KeystoreResult>(`/api/clients/${slug}/keystore/generate`, { method: 'POST' }),
  jobs: (limit = 40) => request<Job[]>(`/api/jobs?limit=${limit}`),
  job: (id: number) => request<Job>(`/api/jobs/${id}`),
  build: (slug: string, options: {
    ref?: string;
    skipSmoke?: boolean;
    debug?: boolean;
    upload?: boolean;
    platform?: 'android' | 'ios';
    requestedAgentId?: number | null;
  }) => request<Job>('/api/jobs', { method: 'POST', body: JSON.stringify({ slug, ...options }) }),
  platforms: () => request<Platforms>('/api/platforms'),
  cancel: (id: number) => request<{ ok: boolean }>(`/api/jobs/${id}/cancel`, { method: 'POST' }),
  devices: () => request<DeviceSlot[]>('/api/devices'),
  storage: () => request<Storage>('/api/storage'),
  sweepStorage: () => request<{ removed: number; bytesFreed: number }>('/api/storage/sweep', { method: 'POST', body: '{}' }),
  deleteJob: (id: number) => request<{ id: number; bytesFreed: number }>(`/api/jobs/${id}`, { method: 'DELETE' }),
  deleteClient: (slug: string) =>
    request<{ slug: string; buildsRemoved: number; bytesFreed: number; keystoreKeptAt: string | null }>(
      `/api/clients/${slug}?confirm=${encodeURIComponent(slug)}`, { method: 'DELETE' },
    ),

  me: () => request<Session>('/api/me'),
  login: (email: string, password: string) =>
    request<{ user: User }>('/api/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: boolean }>('/api/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: boolean }>('/api/me/password', {
      method: 'POST', body: JSON.stringify({ currentPassword, newPassword }),
    }),

  users: () => request<User[]>('/api/users'),
  createUser: (input: { email: string; name?: string; password: string; role: Role }) =>
    request<User>('/api/users', { method: 'POST', body: JSON.stringify(input) }),
  updateUser: (id: number, patch: { role?: Role; disabled?: boolean; password?: string; name?: string }) =>
    request<User>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
};

export interface LiveEvent {
  seq: number;
  type: string;
  payload: Record<string, unknown>;
}

/** Subscribes to one job's event stream. The service replays from the database first. */
export function streamJob(id: number, onEvent: (event: LiveEvent) => void, onEnd: () => void): () => void {
  const source = new EventSource(`/api/jobs/${id}/events`);
  const forward = (type: string) => (message: MessageEvent<string>) =>
    onEvent({ seq: Number(message.lastEventId || 0), type, payload: JSON.parse(message.data) });
  for (const type of ['job.start', 'job.claimed', 'device.leased', 'step.start', 'step.end', 'log',
    'warn', 'meta', 'artifact', 'job.end', 'job.finished', 'job.cancelled', 'job.requeued']) {
    source.addEventListener(type, forward(type) as EventListener);
  }
  source.addEventListener('end', () => {
    source.close();
    onEnd();
  });
  source.onerror = () => source.close();
  return () => source.close();
}
